import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import React from 'react';

import {useAlertPolicies} from 'app/components/Alerts/AlertPolicyProvider';
import {formatCompositeExpression} from 'app/components/Alerts/alertPolicyUtils';
import {
  ALERT_TYPE_CONFIGURATIONS,
  findAlertPolicyForNote,
  getAlertTypeLabel,
} from 'app/components/Alerts/alertTypes';
import {SidebarCard, SidebarCardHeader} from 'app/components/Sidebar';
import {StateApiNote, getAlertNoteType} from 'app/stores/NotesStore';
import * as C from 'app/utils/constants';
import * as imageryUtils from 'app/utils/imageryUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {noteHasUnenrolledLayers} from 'app/utils/noteUtils';

import cs from './NoteCard.module.scss';
import {SceneLocationPopover} from './SceneLocationPopover';
import {UnenrolledLayerMessage} from './UnenrolledLayerMessage';

const AlertCard: React.FunctionComponent<
  React.PropsWithChildren<{
    note: StateApiNote;
    isSelected: boolean;
    isFocused: boolean;
    enrolledLayerKeys?: string[];
    toggleSelected: (id: number | string) => void;
    canModifyAlerts: boolean;
    onSelectNote: (note: StateApiNote) => void;
    onHoverNote: (event: React.MouseEvent<HTMLElement>) => void;
    onUnhoverNote: (event: React.MouseEvent<HTMLElement>) => void;
    onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => void;
    featureGeometry?: geojson.Geometry;
    cardRef?: React.Ref<HTMLDivElement>;
  }>
> = ({
  note,
  isSelected,
  isFocused,
  enrolledLayerKeys,
  toggleSelected,
  canModifyAlerts,
  onSelectNote,
  onHoverNote,
  onUnhoverNote,
  onSelectImageRefs,
  featureGeometry,
  cardRef,
}) => {
  const {alertPolicies} = useAlertPolicies();
  const alertPolicy = findAlertPolicyForNote(note, alertPolicies);

  const shouldShowFastCompare =
    note.imageRefs.every((imageRef) => imageRef.layerKey?.startsWith('S2')) && isFocused;

  const noteType = getAlertNoteType(note);
  const noteConfig = ALERT_TYPE_CONFIGURATIONS[noteType];

  if (note.isCreating || note.isUpdating) {
    return (
      <SidebarCard>
        <B.Spinner className={cs.spinner} size={B.SpinnerSize.SMALL} />
      </SidebarCard>
    );
  }

  const hasUnenrolledLayer = enrolledLayerKeys && noteHasUnenrolledLayers(note, enrolledLayerKeys);

  return (
    <SidebarCard
      ref={cardRef}
      interactive
      isFocused={isFocused}
      onClick={() => onSelectNote(note)}
      onMouseEnter={onHoverNote}
      onMouseLeave={onUnhoverNote}
    >
      <SidebarCardHeader>
        <span className={cs.cardLeading}>
          <B.Checkbox
            onClick={(e) => e.stopPropagation()}
            className={cs.selectCheckbox}
            checked={isSelected}
            onChange={() => toggleSelected(note.id)}
            disabled={!canModifyAlerts}
          />
          <B.Icon icon={noteConfig.icon} size={14} color={noteConfig.color} />
        </span>
        <span className={cs.cardTypeLabel} style={{color: noteConfig.color}}>
          {getAlertTypeLabel(noteType, alertPolicy)}
        </span>
        <span className={cs.cardDate}>{imageryUtils.formatDate(note.createdAt)}</span>
        <span className={cs.cardActions}>
          {shouldShowFastCompare && enrolledLayerKeys && (
            <S2QuickViewMenu
              note={note}
              enrolledLayerKeys={enrolledLayerKeys}
              onSelectImageRefs={onSelectImageRefs}
            />
          )}
          <SceneLocationPopover note={note} featureGeometry={featureGeometry} />
        </span>
      </SidebarCardHeader>

      {hasUnenrolledLayer && <UnenrolledLayerMessage note={note} />}
      <p className={cs.cardText}>{note.text}</p>
      {/* A composite's name says nothing about the rule, and the note opens on truecolor, so
          without the expression the reader has a name over imagery and no way to tell which
          indexes agreed. Rendered from the policy rather than baked into note.text so there's
          one formatter. */}
      {alertPolicy?.alertType === C.CUSTOM_ALERT_TYPE_COMPOSITE && (
        <p className={cs.alertRule}>
          {formatCompositeExpression(alertPolicy.sourceId, alertPolicy.properties)}
        </p>
      )}
    </SidebarCard>
  );
};

// Keeping this component with AlertCard as for now, we only want to use it for alerts with S2 layers
const S2QuickViewMenu: React.FunctionComponent<
  React.PropsWithChildren<{
    note: StateApiNote;
    enrolledLayerKeys: string[];
    onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => void;
  }>
> = ({note, enrolledLayerKeys, onSelectImageRefs}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const menuOptions = React.useMemo(() => {
    return (
      enrolledLayerKeys
        .filter((layerKey) => layerKey.startsWith('S2') && layerKey !== note.imageRefs[0].layerKey)
        .map((layerKey) => {
          return {
            // trim off (S2), they all S2
            label: layerUtils.getLayer(layerKey).display.split(' (S2)')[0],
            value: layerKey,
          };
        })
        // sort truecolor to top, then alphabetical
        .sort((a, b) => {
          if (a.label.includes('Truecolor')) {
            return -1;
          } else if (b.label.includes('Truecolor')) {
            return 1;
          } else {
            return a.label.localeCompare(b.label);
          }
        })
    );
  }, [enrolledLayerKeys, note.imageRefs]);

  return (
    <B.Popover
      position={B.PopoverPosition.RIGHT}
      content={
        /**override blueprint padding so we can make a nice menu header */
        <B.Menu className={cs.s2QuickviewMenu}>
          <div className={cs.s2QuickviewMenuHeader}>
            S2 Quickview
            <B.Button
              icon="cross"
              minimal
              small
              onClick={(e) => {
                e.stopPropagation();
                onSelectImageRefs(note.imageRefs);
                setMenuOpen(false);
              }}
            />
          </div>
          <div className={cs.s2QuickviewMenuDescription}>
            Temporarily view a different S2 layer for this alert’s date(s). Does not save to note.
          </div>
          {menuOptions?.map((option) => (
            <B.MenuItem
              key={option.value}
              text={option.label}
              //override blueprint padding to match our custom header
              style={{padding: '0.5rem 1rem'}}
              onClick={(e) => {
                e.stopPropagation();
                onSelectImageRefs(
                  note.imageRefs.map((imageRef) => ({
                    layerKey: option.value,
                    cursor: imageRef.cursor,
                  })) as mapUtils.MapImageRefs
                );
              }}
            />
          ))}
        </B.Menu>
      }
      isOpen={menuOpen}
      onClose={() => setMenuOpen(false)}
      minimal
    >
      <B.Tooltip content="S2 Quickview">
        <B.AnchorButton
          icon="comparison"
          minimal
          small
          active={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((prev) => !prev);
          }}
        />
      </B.Tooltip>
    </B.Popover>
  );
};

export default AlertCard;
